import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Lock, ArrowRight, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useVerificationDialog } from "@/contexts/VerificationDialogContext";

const initial = {
  full_name: "",
  license_number: "",      // labelled as "Company / Organization"
  tax_id: "",              // optional EIN / Tax ID
  specialty_or_clinic: "", // labelled as "What You Need Sourced"
  whatsapp_number: "",     // labelled as "Phone Number"
};

const fieldDefs = [
  {
    key: "full_name",
    label: "Full Name",
    placeholder: "Jane A. Carter",
    autoComplete: "name",
    required: true,
  },
  {
    key: "license_number",
    label: "Company / Organization",
    placeholder: "Acme Holdings, LLC",
    autoComplete: "organization",
    required: true,
  },
  {
    key: "tax_id",
    label: "EIN / Tax ID",
    placeholder: "12-3456789 (optional)",
    autoComplete: "off",
    required: false,
    helper: "Optional — speeds up invoicing & contract onboarding.",
  },
  {
    key: "specialty_or_clinic",
    label: "What You Need Sourced",
    placeholder: "Industry / product category · estimated volume",
    autoComplete: "off",
    required: true,
  },
  {
    key: "whatsapp_number",
    label: "Phone Number",
    placeholder: "+1 (321) 555 0199",
    autoComplete: "tel",
    required: true,
    type: "tel",
  },
];

function validate(form) {
  const errors = {};
  fieldDefs.forEach(({ key, required }) => {
    if (!required) return;
    const value = (form[key] || "").trim();
    if (!value) {
      errors[key] = "This field is required";
    } else if (value.length < 2) {
      errors[key] = "Please enter at least 2 characters";
    }
  });
  return errors;
}

export default function VerificationFormDialog() {
  const { open, setOpen, closeDialog } = useVerificationDialog();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[k];
        return next;
      });
    }
  }

  function reset() {
    setForm(initial);
    setErrors({});
  }

  async function submit(e) {
    e.preventDefault();
    const trimmed = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, (v || "").trim()])
    );
    const nextErrors = validate(trimmed);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      toast.error("Please complete every required field");
      return;
    }

    // Strip empty optional fields to avoid sending empty strings
    if (!trimmed.tax_id) delete trimmed.tax_id;

    setSubmitting(true);
    try {
      const { data } = await api.post("/physician-verification", trimmed);
      toast.success("Sourcing request received", {
        description: "A trade-liaison advisor will be in touch shortly.",
      });
      closeDialog();
      reset();
      if (data && data.redirect_url) {
        window.open(data.redirect_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error("Submission failed", { description: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 250);
      }}
    >
      <DialogContent
        data-testid="dialog-verification-form"
        className="sm:max-w-xl p-0 overflow-hidden bg-[#0F172A] text-white border border-white/10 rounded-sm max-h-[92vh] overflow-y-auto"
      >
        {/* Header band */}
        <div className="relative bg-[#0F172A] text-white px-7 sm:px-9 pt-8 pb-7 border-b border-white/10">
          <div
            className="absolute inset-0 bg-grid-dark opacity-90 pointer-events-none"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                "radial-gradient(500px 240px at 95% 0%, rgba(180,83,9,0.20), transparent 60%)," +
                "radial-gradient(420px 240px at 0% 100%, rgba(5,150,105,0.18), transparent 60%)",
            }}
          />
          <div className="relative">
            <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-[0.22em] font-bold text-slate-200">
              <Lock size={11} className="text-emerald-400" />
              Confidential B2B Sourcing Intake
            </div>
            <DialogHeader className="mt-4 space-y-2 text-left">
              <DialogTitle className="font-display text-2xl sm:text-[28px] font-semibold tracking-tight text-white">
                Tell Us What You Need Sourced
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-sm leading-relaxed">
                Share a few details about your company and what you need
                sourced from the Dominican Republic. A trade-liaison advisor
                from PS GLOBAL SERIES will follow up to schedule a sourcing
                consultation.
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          noValidate
          className="px-7 sm:px-9 py-7 space-y-5"
          data-testid="verification-form"
        >
          {fieldDefs.map((f) => (
            <DarkField
              key={f.key}
              label={f.label}
              required={f.required}
              helper={f.helper}
              error={errors[f.key]}
              errorTestId={`vf-error-${f.key.replace(/_/g, "-")}`}
            >
              <DarkInput
                data-testid={`vf-${f.key.replace(/_/g, "-")}`}
                value={form[f.key]}
                onChange={(e) => update(f.key, e.target.value)}
                placeholder={f.placeholder}
                type={f.type || "text"}
                autoComplete={f.autoComplete}
                aria-invalid={Boolean(errors[f.key])}
                hasError={Boolean(errors[f.key])}
              />
            </DarkField>
          ))}

          {/* Trust subtitle */}
          <p
            data-testid="verification-trust-text"
            className="text-[12px] italic text-slate-400 leading-relaxed border-t border-white/10 pt-4"
          >
            Your corporate information is strictly protected under US B2B
            confidentiality standards and is used exclusively to qualify and
            scope your sourcing engagement before any commercial conversation
            begins.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400">
              <ShieldCheck size={13} className="text-emerald-400" />
              Encrypted at rest
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="verification-form-submit"
              className="group inline-flex items-center justify-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3.5 text-sm font-semibold tracking-tight rounded-sm transition-all disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Lock size={16} />
              )}
              Submit Sourcing Request
              <ArrowRight
                size={15}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DarkField({ label, required, children, error, errorTestId, helper }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-300">
        {label}{" "}
        {required ? (
          <span className="text-[#F59E0B]">*</span>
        ) : (
          <span className="text-slate-500 normal-case tracking-normal font-medium">
            (optional)
          </span>
        )}
      </Label>
      {children}
      {helper && !error && (
        <div className="text-[11px] text-slate-500 leading-relaxed">
          {helper}
        </div>
      )}
      {error && (
        <div
          data-testid={errorTestId}
          role="alert"
          className="flex items-center gap-1.5 text-[12px] text-[#F59E0B] mt-1"
        >
          <AlertCircle size={12} />
          {error}
        </div>
      )}
    </div>
  );
}

function DarkInput({ hasError, ...props }) {
  return (
    <Input
      {...props}
      className={
        "bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-1 rounded-sm h-11 px-3 transition-colors " +
        (hasError
          ? "border-[#F59E0B] focus-visible:border-[#F59E0B] focus-visible:ring-[#F59E0B]"
          : "border border-white/10 focus-visible:border-[#B45309] focus-visible:ring-[#B45309]")
      }
    />
  );
}
